import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  TextField,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';

interface BulkDeleteDialogProps {
  open: boolean;
  loading: boolean;
  selectedCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export const BulkDeleteDialog: React.FC<BulkDeleteDialogProps> = ({
  open,
  loading,
  selectedCount,
  onConfirm,
  onCancel,
}) => {
  const [confirmationText, setConfirmationText] = useState<string>('');
  const REQUIRED_TEXT = 'ONAYLIYORUM';
  const isConfirmed = confirmationText.trim().toUpperCase() === REQUIRED_TEXT;

  // Modal açıldığında/kapandığında input'u temizle
  useEffect(() => {
    if (!open) {
      setConfirmationText('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (isConfirmed) {
      onConfirm();
      setConfirmationText(''); // İşlem sonrası temizle
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onCancel : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningIcon />
        Toplu Stok Silme
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            ⚠️ UYARI: Bu işlem geri alınamaz!
          </Typography>
        </Alert>

        <DialogContentText sx={{ mb: 2 }}>
          Seçili <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>{selectedCount} adet</Box> stok ürünü kalıcı olarak silinecektir.
        </DialogContentText>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            📋 Silme İşlemi Detayları:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            • Toplam silinecek ürün: <strong>{selectedCount} adet</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            • Ürünler tüm depolardan kaldırılacak
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • İlgili tüm veriler silinecek
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Bu işlem geri alınamaz
          </Typography>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight="bold" color="error.main" gutterBottom>
            İşlemi onaylamak için aşağıdaki kutuya büyük harflerle "<strong>ONAYLIYORUM</strong>" yazın:
          </Typography>
          <TextField
            fullWidth
            autoFocus
            variant="outlined"
            placeholder="ONAYLIYORUM yazın"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            disabled={loading}
            sx={{ mt: 1 }}
            error={confirmationText.length > 0 && !isConfirmed}
            helperText={
              confirmationText.length > 0 && !isConfirmed
                ? 'Lütfen tam olarak "ONAYLIYORUM" yazın (büyük harflerle)'
                : isConfirmed
                ? '✓ Onay metni doğru'
                : ''
            }
            inputProps={{
              style: { textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold' }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onCancel} 
          color="primary"
          disabled={loading}
          variant="outlined"
        >
          İptal
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="error" 
          variant="contained"
          disabled={loading || !isConfirmed}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
        >
          {loading ? 'Siliniyor...' : `${selectedCount} Ürünü Sil`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

